import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { Student } from '@/types';
import StudentForm, {
  emptyStudentForm,
  type StudentFormValues,
} from './components/StudentForm';

function toFormValues(student: Student): StudentFormValues {
  return {
    ...emptyStudentForm,
    name: student.name ?? '',
    school: student.school ?? '',
    grade: student.grade || '중1',
    parentContact: student.parent_contact ?? '',
    lessonLocation: student.lesson_location ?? '',
    targetType: student.target_type || '고등학교',
    targetSchool: student.target_school ?? '',
    desiredMajor: student.desired_major ?? '',
    desiredCareer: student.desired_career ?? '',
    classDays: student.class_days ?? [],
    classTimes: student.class_times ?? {},
    conceptBook: student.concept_book ?? '',
    calcBook: student.calc_book ?? '',
    typeBook: student.type_book ?? '',
    advancedBook: student.advanced_book ?? '',
    completedBooks: student.completed_books ?? '',
    tuitionFee: student.tuition_fee ? String(student.tuition_fee) : '',
    tuitionDueDay: student.tuition_due_day ? String(student.tuition_due_day) : '10',
    firstLessonDate: student.first_lesson_date ?? '',
    goalDeadline: student.goal_deadline ?? '',
    goalProgress: student.goal_progress ?? '',
  };
}

export default function EditStudentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('students')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (fetchError) throw fetchError;
      if (!data) {
        setLoadError('학생 정보를 찾을 수 없어요.');
        return;
      }
      setStudent(data as Student);
    } catch {
      setLoadError('학생 정보를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (values: StudentFormValues) => {
    if (!id) return;
    setError('');
    setSaving(true);
    try {
      const { error: updateError } = await supabase
        .from('students')
        .update({
          name: values.name.trim(),
          school: values.school.trim(),
          grade: values.grade,
          parent_contact: values.parentContact.trim(),
          lesson_location: values.lessonLocation.trim(),
          target_type: values.targetType,
          target_school: values.targetSchool.trim(),
          desired_major: values.desiredMajor.trim(),
          desired_career: values.desiredCareer.trim(),
          class_days: values.classDays,
          class_times: values.classTimes,
          class_start_time: values.classTimes[values.classDays[0]]?.start ?? '',
          class_end_time: values.classTimes[values.classDays[0]]?.end ?? '',
          concept_book: values.conceptBook.trim(),
          calc_book: values.calcBook.trim(),
          type_book: values.typeBook.trim(),
          advanced_book: values.advancedBook.trim(),
          completed_books: values.completedBooks.trim(),
          tuition_fee: values.tuitionFee ? Number(values.tuitionFee) : 0,
          tuition_due_day: values.tuitionDueDay ? Number(values.tuitionDueDay) : 0,
          first_lesson_date: values.firstLessonDate || null,
          goal_deadline: values.goalDeadline || null,
          goal_progress: values.goalProgress || null,
        })
        .eq('id', id);
      if (updateError) throw updateError;
      navigate(`/students/${id}`);
    } catch {
      setError('저장에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin text-xl"></i>
        불러오는 중...
      </div>
    );
  }

  if (loadError || !student) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-16 md:px-6">
        <div className="flex flex-col items-center gap-3 rounded-xl border border-background-200 bg-background-100 py-16">
          <i className="ri-error-warning-line text-2xl text-accent-600"></i>
          <p className="text-sm text-foreground-700">{loadError || '학생 정보를 찾을 수 없어요.'}</p>
          <button
            type="button"
            onClick={load}
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 md:px-6 md:py-10">
      <button
        type="button"
        onClick={() => navigate(`/students/${student.id}`)}
        className="flex items-center gap-1 text-sm font-medium text-foreground-600 hover:text-foreground-900 whitespace-nowrap"
      >
        <i className="ri-arrow-left-line"></i>
        학생관리카드
      </button>

      <h1 className="mt-4 text-2xl font-bold text-foreground-950">학생 정보 수정</h1>
      <p className="mt-1.5 text-sm text-foreground-600">
        {student.name} 학생의 기본 정보·수업 일정·교재·교습비·목표 진도를 수정할 수 있어요.
      </p>

      <StudentForm
        initialValues={toFormValues(student)}
        submitLabel="수정 저장하기"
        submitIcon="ri-save-line"
        submitting={saving}
        error={error}
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/students/${student.id}`)}
      />
    </div>
  );
}